/** Drive Label selector — shown on folder picker after a folder is selected. */

import { useEffect, useRef } from "react";
import { Box, Typography, TextField, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LabelIcon from "@mui/icons-material/Label";
import { useQuery } from "@tanstack/react-query";
import { getConfig } from "@/auth/config";
import { getUserManager } from "@/hooks/useAuth";

interface DriveLabelSelectorProps {
  value: Record<string, Record<string, string>>;
  onChange: (labels: Record<string, Record<string, string>>) => void;
}

interface LabelConfig {
  mandatoryLabelId: string;
  defaultClassification: string;
  availableLabelIds: string[];
}

async function fetchLabelConfig(): Promise<LabelConfig> {
  const config = getConfig();
  const base = config?.apiEndpoint ?? "";
  const um = getUserManager();
  const user = await um?.getUser();
  const token = user?.id_token;

  const res = await fetch(`${base}/admin/reporting/settings`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return { mandatoryLabelId: "", defaultClassification: "", availableLabelIds: [] };
  const data = await res.json();
  return {
    mandatoryLabelId: data.mandatoryLabelId ?? "",
    defaultClassification: data.defaultClassification ?? "",
    availableLabelIds: data.availableLabelIds ?? [],
  };
}

export default function DriveLabelSelector({ value, onChange }: DriveLabelSelectorProps) {
  const { data: config } = useQuery({
    queryKey: ["drive-label-config"],
    queryFn: fetchLabelConfig,
    staleTime: 300_000,
  });

  const initializedRef = useRef(false);

  // Initialize mandatory label with default value once when config loads
  useEffect(() => {
    if (initializedRef.current) return;
    if (!config?.mandatoryLabelId || !config.defaultClassification) return;
    initializedRef.current = true;
    onChange({
      ...value,
      [config.mandatoryLabelId]: { classification: config.defaultClassification },
    });
  }, [config?.mandatoryLabelId, config?.defaultClassification, value, onChange]);

  if (!config?.mandatoryLabelId && !config?.availableLabelIds?.length) {
    return null; // No labels configured
  }

  const allLabelIds = [
    ...(config.mandatoryLabelId ? [config.mandatoryLabelId] : []),
    ...(config.availableLabelIds?.filter((id) => id !== config.mandatoryLabelId) ?? []),
  ];

  const updateField = (labelId: string, fieldId: string, fieldValue: string) => {
    const current = value[labelId] ?? {};
    onChange({
      ...value,
      [labelId]: { ...current, [fieldId]: fieldValue },
    });
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
        <LabelIcon fontSize="inherit" /> Drive Labels
      </Typography>
      {allLabelIds.map((labelId) => {
        const isMandatory = labelId === config.mandatoryLabelId;
        return (
          <Accordion key={labelId} defaultExpanded={isMandatory} variant="outlined" disableGutters sx={{ "&:before": { display: "none" }, mb: 0.5 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, "& .MuiAccordionSummary-content": { my: 0.5 } }}>
              <Typography variant="body2">
                {labelId}{isMandatory && " (required)"}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <TextField
                label="Classification"
                value={value[labelId]?.classification ?? ""}
                onChange={(e) => updateField(labelId, "classification", e.target.value)}
                size="small"
                fullWidth
                placeholder={isMandatory ? config.defaultClassification : "Optional"}
                required={isMandatory}
              />
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
